/**
 * UI strings. Keys are the English source strings, exactly as Swift's L("…").
 * Values come from src/i18n/<locale>.json generated by tools/export-xcstrings.mjs
 * (placeholders `{0}`, `{1}`; plurals as objects keyed by CLDR category).
 */
export const SUPPORTED_CONTENT_LOCALES = ['en', 'fr', 'de', 'es', 'pt-BR', 'it', 'pl'] as const;
export type ContentLocale = (typeof SUPPORTED_CONTENT_LOCALES)[number];
export type LanguageSelection = 'automatic' | ContentLocale;

type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>>;
export type Catalog = Record<string, string | PluralForms>;

const catalogs: Partial<Record<ContentLocale, Catalog>> = {};

/** Locales with a hand-written web-only catalog (src/i18n/web/<locale>.json). pl falls back to en. */
export const WEB_CATALOG_LOCALES: readonly ContentLocale[] = ['en', 'fr', 'de', 'es', 'it', 'pt-BR'];

/**
 * Web-only strings (no iOS key, or a literal iOS never localizes): merged under the generated
 * catalog, so an iOS translation always wins once the key reaches Localizable.xcstrings.
 */
export function mergeWebCatalog(generated: Catalog, web: Catalog | undefined): Catalog {
  return web ? { ...web, ...generated } : generated;
}

async function loadWebCatalog(locale: ContentLocale): Promise<Catalog | undefined> {
  if (!WEB_CATALOG_LOCALES.includes(locale)) return undefined;
  const mod = (await import(`../i18n/web/${locale}.json`)) as { default?: Catalog } & Catalog;
  return (mod.default ?? mod) as Catalog;
}

/**
 * Generated catalogs (src/i18n/<locale>.json, `en` = identity catalog plus English plural forms)
 * merged with the web-only catalog (src/i18n/web/<locale>.json).
 */
export async function loadCatalog(locale: ContentLocale): Promise<Catalog> {
  if (catalogs[locale]) return catalogs[locale]!;
  const [mod, web] = await Promise.all([
    import(`../i18n/${locale}.json`) as Promise<{ default?: Catalog } & Catalog>,
    loadWebCatalog(locale).catch(() => undefined),
  ]);
  catalogs[locale] = mergeWebCatalog((mod.default ?? mod) as Catalog, web);
  return catalogs[locale]!;
}

export type Translate = (key: string, params?: Record<string, string | number> | (string | number)[]) => string;

const SWIFT_PLACEHOLDER = /%%|%(\d+\$)?(@|lld|ld|d|f|\.\d+f)/g;

/**
 * Swift format key → catalog template: `%@`/`%lld` become `{0}`, `{1}`… in order,
 * positional `%2$@` become `{1}`, `%%` becomes `%`. Pure (no shared counter).
 */
export function swiftKeyToTemplate(key: string): string {
  let next = 0;
  return key.replace(SWIFT_PLACEHOLDER, (match, pos: string | undefined) => {
    if (match === '%%') return '%';
    return `{${pos ? Number(pos.slice(0, -1)) - 1 : next++}}`;
  });
}

function fillTemplate(template: string, list: readonly (string | number)[]): string {
  return template.replace(/\{(\d+)\}/g, (_m, i: string) => String(list[Number(i)] ?? ''));
}

/**
 * `t(key, params)`. Lookup order: `catalog[key]`, then `fallback[key]` (pass the English
 * catalog so plural keys still resolve), then the key itself as a template.
 * Plural entries pick the form from the first numeric param.
 */
export function makeTranslate(locale: ContentLocale, catalog: Catalog, fallback?: Catalog): Translate {
  const rules = new Intl.PluralRules(locale === 'pt-BR' ? 'pt' : locale);
  const fallbackRules = new Intl.PluralRules('en');
  return (key, params) => {
    const list = Array.isArray(params) ? params : params ? Object.values(params) : [];
    let entry = catalog[key];
    let pluralRules = rules;
    if (entry == null && fallback) { entry = fallback[key]; pluralRules = fallbackRules; }
    let template: string;
    if (entry == null) template = swiftKeyToTemplate(key);
    else if (typeof entry === 'string') template = entry;
    else {
      const n = Number(list.find((v) => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))) ?? 0);
      template = entry[pluralRules.select(n)] ?? entry.other ?? swiftKeyToTemplate(key);
    }
    return fillTemplate(template, list);
  };
}

/**
 * Port of LanguageStore.resolveEffectiveLanguage. Like iOS, only the first preferred
 * language counts (`navigator.languages[0]`): exact tag, then `pt-*` → pt-BR except
 * Portugal (`pt-PT` → en), then the base language, else en.
 */
export function resolveEffectiveLocale(selection: LanguageSelection, deviceLanguages: readonly string[], available: ReadonlySet<string>): ContentLocale {
  if (selection !== 'automatic' && available.has(selection)) return selection;
  const byNormalized = new Map<string, string>();
  for (const code of available) byNormalized.set(code.toLowerCase(), code);
  const pick = (code: string | undefined): ContentLocale => (code && isContentLocale(code) ? code : 'en');
  const tag = (deviceLanguages[0] ?? 'en').replaceAll('_', '-').toLowerCase();
  const exact = byNormalized.get(tag);
  if (exact) return pick(exact);
  const [language = 'en', ...rest] = tag.split('-');
  if (language === 'pt') return rest.includes('pt') ? 'en' : pick(byNormalized.get('pt-br'));
  return pick(byNormalized.get(language));
}

export function isContentLocale(v: string): v is ContentLocale {
  return (SUPPORTED_CONTENT_LOCALES as readonly string[]).includes(v);
}

export const LANGUAGE_NATIVE_NAMES: Record<ContentLocale, string> = { en: 'English', fr: 'Français', de: 'Deutsch', es: 'Español', 'pt-BR': 'Português (Brasil)', it: 'Italiano', pl: 'Polski' };
export const LANGUAGE_FLAGS: Record<ContentLocale, string> = { en: '🇺🇸', fr: '🇫🇷', de: '🇩🇪', es: '🇪🇸', 'pt-BR': '🇧🇷', it: '🇮🇹', pl: '🇵🇱' };
export const SELECTION_KEY = 'teacheck.language.selection';
export const AVAILABLE_KEY = 'teacheck.language.available-languages';
